/**
 * One editable line of a template — the subject, or one paragraph of the body
 * (D10).
 *
 * TEXT AND PLACEHOLDERS, NEVER CODE. The paragraph is a row of segments: text
 * runs, each in its own growing text field, and placeholders between them as
 * chips. A chip is one unit — it can be removed (its button, or Backspace /
 * Delete right next to it) but not typed into, so no amount of typing turns
 * text into a placeholder or a placeholder into syntax. Braces typed into a
 * text field stay text.
 *
 * THE FIELDS GROW WITH THEIR TEXT. Each text field sits in a one-cell grid
 * beside an invisible copy of its own text, so the copy sizes the cell and the
 * field fills it (`cols={1}` keeps the textarea's own default width out of
 * it): the row reads like a line of text with chips in it, wraps like one,
 * and needs no measuring script. The focus is shown once, by the ring of the
 * whole row, not again by the global textarea outline.
 *
 * THE CARET IS REPORTED, NOT OWNED. Every focus, selection and keystroke tells
 * the tab where the caret is (`onTarget`), because that is where a placeholder
 * from the catalogue goes and what the formatting buttons act on. Focus moves
 * that the editor causes (after inserting or removing a chip) come back in via
 * `registerField`.
 */
import React from "react";
import { X } from "lucide-react";

import { placeholderLabel } from "./automationTemplateLabels";

function markClasses(marks) {
  return [
    marks.includes("strong") ? "font-semibold" : "",
    marks.includes("emphasis") ? "italic" : "",
    marks.includes("underline") ? "underline" : "",
  ].join(" ");
}

function TextPiece({
  segment,
  label,
  singleLine,
  grow,
  readOnly,
  registerField,
  onChange,
  onTarget,
  onKeyDown,
}) {
  const report = (event) => {
    const el = event.currentTarget;
    onTarget(segment.id, el.selectionStart ?? 0, el.selectionEnd ?? 0);
  };
  const typeClasses = `text-[13px] leading-6 ${markClasses(segment.marks)}`;
  return (
    <span
      className={`inline-grid min-w-[2ch] max-w-full align-top ${grow ? "flex-1 basis-24" : "flex-none"}`}
    >
      <span
        aria-hidden="true"
        className={`invisible col-start-1 row-start-1 whitespace-pre-wrap break-words px-0.5 ${typeClasses}`}
      >
        {`${segment.text} `}
      </span>
      <textarea
        ref={(el) => registerField(segment.id, el)}
        rows={1}
        cols={1}
        value={segment.text}
        readOnly={readOnly}
        aria-label={label}
        spellCheck
        onChange={(event) => {
          onChange(segment.id, event.target.value);
          report(event);
        }}
        onFocus={report}
        onSelect={report}
        onKeyUp={report}
        onClick={report}
        onKeyDown={(event) => onKeyDown(event, segment)}
        className={`col-start-1 row-start-1 w-full resize-none overflow-hidden whitespace-pre-wrap break-words border-0 bg-transparent px-0.5 text-foreground outline-none placeholder:text-muted-foreground focus-visible:outline-none ${typeClasses}`}
        data-single-line={singleLine ? "true" : undefined}
      />
    </span>
  );
}

function Chip({ segment, readOnly, onRemove }) {
  const label = placeholderLabel(segment.key);
  return (
    <span
      data-placeholder-key={segment.key}
      className={`inline-flex max-w-full items-center gap-1 self-start rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[12px] leading-5 text-primary ${markClasses(segment.marks)}`}
    >
      <span className="break-all">{label}</span>
      {readOnly ? null : (
        <button
          type="button"
          onClick={() => onRemove(segment.id)}
          aria-label={`Platzhalter ${label} entfernen`}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-primary max-md:h-6 max-md:w-6 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

export default function AutomationTemplateField({
  paragraph,
  label,
  singleLine = false,
  readOnly = false,
  registerField,
  onTextChange,
  onTarget,
  onRemoveVariable,
  onNavigate,
}) {
  const segments = paragraph.segments;
  const textIds = segments.filter((s) => s.kind === "text").map((s) => s.id);
  const lastTextId = textIds[textIds.length - 1];

  const handleKeyDown = (event, segment) => {
    const el = event.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const index = segments.findIndex((s) => s.id === segment.id);
    const previous = segments[index - 1];
    const next = segments[index + 1];
    if (singleLine && event.key === "Enter") {
      event.preventDefault();
      return;
    }
    if (readOnly || start !== end) return;
    if (event.key === "Backspace" && start === 0 && previous?.kind === "variable") {
      event.preventDefault();
      onRemoveVariable(previous.id);
    } else if (
      event.key === "Delete" &&
      start === segment.text.length &&
      next?.kind === "variable"
    ) {
      event.preventDefault();
      onRemoveVariable(next.id);
    } else if (event.key === "ArrowLeft" && start === 0 && index > 1) {
      const target = segments[index - 2];
      if (target?.kind === "text") {
        event.preventDefault();
        onNavigate(target.id, target.text.length);
      }
    } else if (
      event.key === "ArrowRight" &&
      start === segment.text.length &&
      index < segments.length - 2
    ) {
      const target = segments[index + 2];
      if (target?.kind === "text") {
        event.preventDefault();
        onNavigate(target.id, 0);
      }
    }
  };

  return (
    <div
      role="group"
      aria-label={label}
      className="flex min-h-[40px] w-full min-w-0 flex-wrap items-start gap-x-0.5 gap-y-1 rounded-[10px] border border-border bg-background px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-ring"
    >
      {segments.map((segment) => {
        if (segment.kind === "variable") {
          return (
            <Chip
              key={segment.id}
              segment={segment}
              readOnly={readOnly}
              onRemove={onRemoveVariable}
            />
          );
        }
        const position = textIds.indexOf(segment.id);
        const pieceLabel =
          textIds.length > 1 ? `${label}, Text ${position + 1} von ${textIds.length}` : label;
        return (
          <TextPiece
            key={segment.id}
            segment={segment}
            label={pieceLabel}
            singleLine={singleLine}
            grow={segment.id === lastTextId}
            readOnly={readOnly}
            registerField={registerField}
            onChange={onTextChange}
            onTarget={onTarget}
            onKeyDown={handleKeyDown}
          />
        );
      })}
    </div>
  );
}
